import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type ExternalLinksClickableContextValue = {
  areExternalLinksClickable: boolean;
};

const defaultContextValue: ExternalLinksClickableContextValue = {
  areExternalLinksClickable: false,
};

const ExternalLinksClickableContext = createContext<ExternalLinksClickableContextValue>(defaultContextValue);

export type ExternalLinksClickableProviderProps = {
  areExternalLinksClickable: boolean;
  children: ReactNode;
};

/**
 * Whether user-generated outbound URLs in this tree may be clickable.
 *
 * Private app layouts pass `true`. Public share-link layouts pass `isOnPaidPlan === true`
 * (missing/undefined fails closed).
 */
export const ExternalLinksClickableProvider = memo(
  ({ areExternalLinksClickable, children }: ExternalLinksClickableProviderProps) => {
    const contextValue = useMemo(() => ({ areExternalLinksClickable }), [areExternalLinksClickable]);

    return (
      <ExternalLinksClickableContext.Provider value={contextValue}>{children}</ExternalLinksClickableContext.Provider>
    );
  },
);

ExternalLinksClickableProvider.displayName = 'ExternalLinksClickableProvider';

export const useExternalLinksClickableContext = () => {
  const ctx = useContext(ExternalLinksClickableContext);

  if (ctx === defaultContextValue) {
    const error = new Error('useExternalLinksClickableContext must be used within ExternalLinksClickableProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'ExternalLinksClickable context not found',
    });
  }

  return ctx;
};
